import { supabase } from '@/lib/supabase'
import type {
  ContactRow,
  ContactInsert,
  ContactUpdate,
  ContactTableRow,
} from '@/types/contacts'
import type { RecordStatusFilter } from '@/types/record-status'
import { applyRecordStatusFilter } from './helpers/apply-record-status-filter'
import { restoreRecord } from './helpers/restore-record'

/**
 * Contact with client info for all contacts view
 */
export interface ContactWithClient extends ContactTableRow {
  clients: {
    id: string
    client_type: string
    company_name: string | null
    first_name: string | null
    last_name: string | null
  } | null
}

/**
 * Fetch all contacts for a client
 */
export async function fetchContacts(
  clientId: string,
  recordStatus: RecordStatusFilter = 'active'
): Promise<ContactTableRow[]> {
  let query = supabase
    .from('contacts')
    .select(
      'id, client_id, first_name, last_name, job_title, email_primary, phone_mobile, phone_office, is_primary_contact, is_billing_contact, is_operations_contact, status, created_at, deleted_at'
    )
    .eq('client_id', clientId)

  query = applyRecordStatusFilter(query, recordStatus)

  const { data, error } = await query
    .order('is_primary_contact', { ascending: false })
    .order('created_at', { ascending: false })

  if (error) throw error
  return (data as ContactTableRow[]) || []
}

/**
 * Fetch all contacts across all clients
 */
export async function fetchAllContacts(
  recordStatus: RecordStatusFilter = 'active'
): Promise<ContactWithClient[]> {
  let query = supabase
    .from('contacts')
    .select(
      `id, client_id, first_name, last_name, job_title, email_primary, phone_mobile, phone_office, is_primary_contact, is_billing_contact, is_operations_contact, status, created_at, deleted_at,
       clients:client_id (id, client_type, company_name, first_name, last_name)`
    )

  query = applyRecordStatusFilter(query, recordStatus)

  const { data, error } = await query.order('created_at', { ascending: false })

  if (error) throw error
  return (data as ContactWithClient[]) || []
}

/**
 * Fetch a single contact by ID
 */
export async function fetchContact(id: string): Promise<ContactRow> {
  const { data, error } = await supabase
    .from('contacts')
    .select('*')
    .eq('id', id)
    .single()

  if (error) throw error
  return data as ContactRow
}

/**
 * Create a new contact
 */
export async function createContact(data: ContactInsert): Promise<ContactRow> {
  const { data: created, error } = await supabase
    .from('contacts')
    .insert({
      client_id: data.client_id,
      organization_id: data.organization_id,
      first_name: data.first_name,
      last_name: data.last_name,
      job_title: data.job_title ?? null,
      email_primary: data.email_primary,
      email_secondary: data.email_secondary ?? null,
      phone_office: data.phone_office ?? null,
      phone_mobile: data.phone_mobile ?? null,
      phone_other: data.phone_other ?? null,
      is_primary_contact: data.is_primary_contact ?? false,
      is_billing_contact: data.is_billing_contact ?? false,
      is_operations_contact: data.is_operations_contact ?? false,
      preferred_contact_method: data.preferred_contact_method ?? 'email',
      status: data.status ?? 'active',
      notes: data.notes ?? null,
    })
    .select()
    .single()

  if (error) throw error
  return created as ContactRow
}

/**
 * Update an existing contact
 */
export async function updateContact(
  id: string,
  data: ContactUpdate
): Promise<ContactRow> {
  const updateData: Record<string, unknown> = {}

  if (data.first_name !== undefined) updateData.first_name = data.first_name
  if (data.last_name !== undefined) updateData.last_name = data.last_name
  if (data.job_title !== undefined) updateData.job_title = data.job_title
  if (data.email_primary !== undefined) updateData.email_primary = data.email_primary
  if (data.email_secondary !== undefined) updateData.email_secondary = data.email_secondary
  if (data.phone_office !== undefined) updateData.phone_office = data.phone_office
  if (data.phone_mobile !== undefined) updateData.phone_mobile = data.phone_mobile
  if (data.phone_other !== undefined) updateData.phone_other = data.phone_other
  if (data.is_primary_contact !== undefined)
    updateData.is_primary_contact = data.is_primary_contact
  if (data.is_billing_contact !== undefined)
    updateData.is_billing_contact = data.is_billing_contact
  if (data.is_operations_contact !== undefined)
    updateData.is_operations_contact = data.is_operations_contact
  if (data.preferred_contact_method !== undefined)
    updateData.preferred_contact_method = data.preferred_contact_method
  if (data.status !== undefined) updateData.status = data.status
  if (data.notes !== undefined) updateData.notes = data.notes

  const { data: updated, error } = await supabase
    .from('contacts')
    .update(updateData)
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return updated as ContactRow
}

/**
 * Soft delete a contact (sets deleted_at timestamp)
 */
export async function deleteContact(id: string): Promise<void> {
  const { error } = await supabase
    .from('contacts')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id)

  if (error) throw error
}

/**
 * Restore a soft-deleted contact
 */
export async function restoreContact(id: string): Promise<void> {
  return restoreRecord('contacts', id)
}
